import { Button, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  getGameSystems,
  handlePagingAll
} from "../../../../components/api/APIUtilities";
import {
  BCPArrowUpArrowDown,
  BCPChartMixed,
  BCPDiceFive,
  BCPFilter,
  BCPPlus,
  BCPXmark
} from "../../../../components/icons/bcp/BCPIcons";
import InfoContainer from "../../../../components/layout/InfoContainer";
import OptionsMenu from "../../../../components/layout/OptionsMenu";
import ListResponsive from "../../../../components/other/ListResponsive";
import { postNotification } from "../../../../components/utilities/Notifications";
import {
  clearFilterSx,
  filterAndSortRankings,
  getFilterSx,
  updateFilters
} from "../../../../components/utilities/SortFilterFunctions";
import { getData } from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { RankingCard } from "./OrgDashOverview";

export default function OrgDashRankings(props) {
  const {
    allLeaderboards,
    setAllLeaderboards,
    organization,
    organize,
    gameSystems,
    setGameSystems
  } = props;

  const { organizationId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const [subLoading, setSubLoading] = useState(!allLeaderboards ? true : false);

  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [filteredLeaderboards, setFilteredLeaderboards] = useState(null);

  const fetchLeagues = async (active = true) => {
    const apiLeagues = await handlePagingAll("/leagues", {
      gameStoreId: organization?.id || organizationId,
      "expand[]": ["gameSystem"],
      active: active
    });

    return apiLeagues.sort(sortLeagues);
  };

  const fetchGameSystems = async () => {
    const apiGameSystems = await getGameSystems("/gameSystems", {
      inclSystem: true
    });

    return apiGameSystems;
  };

  const parallel = async () => {
    return {
      taskActiveLeagues: await fetchLeagues(true),
      taskInactiveLeagues: await fetchLeagues(false),
      taskGameSystems: await fetchGameSystems()
    };
  };

  const parallelLeagues = async () => {
    return {
      taskActiveLeagues: await fetchLeagues(true),
      taskInactiveLeagues: await fetchLeagues(false)
    };
  };

  const createLeaderboards = async (leagues) => {
    if (leagues?.length) {
      const leaderboards = [];

      for (const league of leagues) {
        try {
          const apiLeaderboard = await getData("/placings", {
            placingsType: league?.clubLeague ? "team" : "player",
            limit: 5,
            leagueId: league.id,
            sortAscending: false
          });

          leaderboards.push({
            league,
            leaderboards: apiLeaderboard.data
          });
        } catch {}
      }

      return leaderboards;
    } else return [];
  };

  const sortLeagues = (a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    if (a?.endDate && b?.endDate) {
      const endDateA = moment(a.endDate);
      const endDateB = moment(b.endDate);

      if (endDateA.isBefore(endDateB)) return -1;
      if (endDateA.isAfter(endDateB)) return 1;
      if (endDateA.isSame(endDateB)) {
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      }
    } else if (a?.endDate && !b?.endDate) {
      return 1;
    } else if (!a?.endDate && b?.endDate) {
      return -1;
    } else {
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    }
  };

  const pageSetup = async () => {
    try {
      const parallelData = await parallel();

      if (parallelData?.taskGameSystems?.length) {
        setGameSystems(parallelData.taskGameSystems);
      }

      const leagues = [];
      if (parallelData?.taskActiveLeagues?.length) {
        leagues.push(...parallelData.taskActiveLeagues);
      }
      if (parallelData?.taskInactiveLeagues?.length) {
        leagues.push(...parallelData.taskInactiveLeagues);
      }

      const apiLeaderboards = await createLeaderboards(leagues);

      if (apiLeaderboards?.length) {
        setAllLeaderboards(apiLeaderboards);
      } else {
        setAllLeaderboards([]);
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      setSubLoading(false);
    }
  };

  const handleMissingLeaderboards = async () => {
    try {
      const parallelData = await parallelLeagues();

      const leagues = [];

      if (parallelData?.taskActiveLeagues?.length) {
        leagues.push(...parallelData.taskActiveLeagues);
      }
      if (parallelData?.taskInactiveLeagues?.length) {
        leagues.push(...parallelData.taskInactiveLeagues);
      }

      const apiLeaderboards = await createLeaderboards(leagues);

      if (apiLeaderboards?.length) {
        setAllLeaderboards(apiLeaderboards);
      } else {
        setAllLeaderboards([]);
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    if (!allLeaderboards?.length && !gameSystems?.length) {
      pageSetup();
    } else if (!allLeaderboards?.length && gameSystems?.length) {
      handleMissingLeaderboards();
    }
    // eslint-disable-next-line
  }, []);

  // Filter and sort events
  useEffect(() => {
    if (allLeaderboards?.length && filters.length) {
      const tempLeaderboards = filterAndSortRankings(allLeaderboards, filters);

      setFilteredLeaderboards(tempLeaderboards);
    } else if (!filters.length) {
      setFilteredLeaderboards(null);
    }
    // eslint-disable-next-line
  }, [filters, allLeaderboards]);

  return (
    <Grid Grid spacing={1} sx={{ pt: 1, pb: 2, pr: [0, 2], pl: [0, 2] }}>
      <Grid item xs={12}>
        <Typography
          variant="h2"
          component="h3"
          gutterBottom
          sx={{ fontWeight: 700, mb: 2, p: 1 }}
        >
          Leaderboards
        </Typography>
      </Grid>

      <Grid item xs={12}>
        {subLoading || allLeaderboards?.length ? (
          <ListResponsive
            name="Leaderboards"
            originalList={
              subLoading
                ? null
                : filteredLeaderboards !== null
                  ? filteredLeaderboards
                  : allLeaderboards
                    ? allLeaderboards
                    : []
            }
            actionButtonBottom={[
              {
                label: "Sort",
                startIcon: <BCPArrowUpArrowDown />,
                onClick: (e) => {
                  setAnchorEl(e.currentTarget);
                },
                disabled: subLoading || !allLeaderboards.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("sort"))
                    ),
                    filters,
                    theme
                  )
                }
              },
              {
                label: "Filters",
                startIcon: <BCPFilter />,
                onClick: (e) => {
                  setAnchorEl2(e.currentTarget);
                },
                disabled: subLoading || !allLeaderboards.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("filter"))
                    ),
                    filters,
                    theme
                  )
                }
              },
              getEnvironmentVariable("SINGLE_GAME_SYSTEM") !== "true" &&
                getEnvironmentVariable("SINGLE_GAME_MANUFACTURER") !==
                  "true" && {
                  label: "Game Systems",
                  startIcon: <BCPDiceFive />,
                  disabled:
                    subLoading ||
                    !allLeaderboards.length ||
                    !gameSystems?.length,
                  onClick: (e) => {
                    setAnchorEl3(e.currentTarget);
                  },
                  sx: {
                    ...getFilterSx(
                      Boolean(
                        filters.length &&
                          filters.some((item) => item.includes("gamesystem"))
                      ),
                      filters,
                      theme
                    )
                  }
                },
              {
                label: "Clear Filters",
                startIcon: <BCPXmark />,

                disabled: subLoading || !allLeaderboards.length,
                sx: {
                  ...clearFilterSx(false, filters, theme)
                },
                onClick: () => {
                  updateFilters(null, null, null, setFilters);
                  setFilteredLeaderboards(null);
                  setAnchorEl(null);
                  setAnchorEl2(null);
                  setAnchorEl3(null);
                }
              }
            ]}
            doSearch
            searchProps={{
              label: "Search Leaderboards",
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  const filteredList = originalList.filter(function (el) {
                    let found = false;

                    if (el?.league?.name?.toLowerCase().includes(searchText))
                      found = true;
                    if (
                      el?.league?.gameSystem?.name
                        ?.toLowerCase()
                        .includes(searchText)
                    )
                      found = true;

                    if (el?.leaderboards?.length) {
                      for (const player of el.leaderboards) {
                        if (
                          player?.user?.firstName
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          found = true;

                        if (
                          player?.user?.lastName
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          found = true;
                      }
                    }

                    return found;
                  });
                  return filteredList;
                }
              }
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                  <RankingCard row={row} theme={theme} organize={organize} />
                </Grid>
              )
            }}
          />
        ) : !subLoading && !allLeaderboards?.length ? (
          <InfoContainer
            title="No leaderboards."
            body={
              organize
                ? "There are currently no leaderboards. Create an event or circuit to get started!"
                : "There are currently no leaderboards. Check back later!"
            }
            ActionButton={
              <Button
                variant="contained"
                onClick={(e) => {
                  navigate(
                    organize
                      ? `/organize/event?orgId=${organization?.id}`
                      : `/rankings`
                  );
                }}
                startIcon={organize ? <BCPPlus /> : <BCPChartMixed />}
              >
                {organize ? "Create Event" : "View My Leaderboards"}
              </Button>
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <></>
        )}

        {/* Sorting */}
        <OptionsMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          items={[
            {
              name: "sort-name-asc",
              label: "Name Ascending",
              selected: filters.includes("sort-name-asc"),
              onClick: () =>
                updateFilters("sort-name-asc", false, filters, setFilters)
            },
            {
              name: "sort-name-desc",
              label: "Name Descending",
              selected: filters.includes("sort-name-desc"),
              onClick: () =>
                updateFilters("sort-name-desc", false, filters, setFilters)
            }
          ]}
          name="events-sort-menu"
        />

        {/* Filters */}
        <OptionsMenu
          anchorEl={anchorEl2}
          setAnchorEl={setAnchorEl2}
          items={[
            {
              name: "filter-active",
              label: "Active League",
              selected: filters.includes("filter-active"),
              onClick: () =>
                updateFilters(
                  "filter-active",
                  "filter-exclude-active",
                  filters,
                  setFilters
                )
            },
            {
              name: "filter-exclude-active",
              label: "Inactive League",
              selected: filters.includes("filter-exclude-active"),
              onClick: () =>
                updateFilters(
                  "filter-exclude-active",
                  "filter-active",
                  filters,
                  setFilters
                )
            },
            { divider: true },

            {
              name: "filter-hobby",
              label: "Hobby League",
              selected: filters.includes("filter-hobby"),
              onClick: () =>
                updateFilters(
                  "filter-hobby",
                  "filter-exclude-hobby",
                  filters,
                  setFilters
                )
            },
            {
              name: "filter-exclude-hobby",
              label: "Exclude Hobby League",
              selected: filters.includes("filter-exclude-hobby"),
              onClick: () =>
                updateFilters(
                  "filter-exclude-hobby",
                  "filter-hobby",
                  filters,
                  setFilters
                )
            }
          ]}
          name="events-filter-menu"
        />

        {/* Game Systems */}
        {gameSystems?.length && (
          <OptionsMenu
            anchorEl={anchorEl3}
            setAnchorEl={setAnchorEl3}
            items={gameSystems.map((gameSystem) => ({
              name: `gamesystem-${gameSystem?.id}`,
              label: gameSystem.name,
              selected: filters.includes(`gamesystem-${gameSystem?.id}`),
              onClick: () =>
                updateFilters(
                  `gamesystem-${gameSystem?.id}`,
                  false,
                  filters,
                  setFilters
                )
            }))}
            name="gamesystem-menu"
          />
        )}
      </Grid>
    </Grid>
  );
}
